// The Stock page (truck stock, concept A, BL-203): the approved page's parts (ui-web's StockTable,
// CountReview, RefillLines, RestockReceived, ReceiptFiling, WorkList, StatusBar, Figures) fed from the
// real actions' answers, and their decisions back into the actions' inputs. Kept apart from the screen
// (StockPage.tsx) so it is tested. Quantities stay strings as stock.levels gives them ("5.000"); money
// in cents; our cost only where the answer carries it (whoever sees our cost).
import type {
  CatalogMatch,
  CountDecision,
  CountExtra,
  CountLine,
  FigureItem,
  ReceiptDestination,
  ReceiptFilingDecision,
  ReceiptLine,
  RefillLine,
  RestockIssue,
  StatusTab,
} from '@flowology/ui-web/fl';
import { hasNoMin, isBelowMin, qtyWords } from '@flowology/ui-web/fl';
import { money, type StockItemRow, type StockLevel, type StockPlace } from '@flowology/ui-web';
import { orderLocations, type Level, type StockLocation } from './model.ts';
import type { OpenCount } from './truck-counts-model.ts';

/** stock.levels' level, with our moving average cost for whoever sees our cost. */
export interface LevelAnswer extends Level {
  avgCostCents?: string;
}

/** A part as stock.catalog gives it, for what stock.levels doesn't say (its code, unit and category). */
export interface CatalogEntry {
  id: string;
  code: string;
  name: string;
  unit: string;
  category: { id: string; name: string } | null;
  /** What the price book says one costs: only for whoever sees our cost. */
  costCents?: number | null;
}

/** An item the catalog didn't give (not read, or not a part): its own group, after the others. */
export const NO_CATEGORY = { id: 'none', name: 'Not in a category' };

// ── words ──

/** "Sam Rivera" → "Sam". */
export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;
/** "Sam Rivera" → "Sam R."; one name stays as it is. */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}.` : name.trim();
}

const civil = (s: string) =>
  s
    .replace('AM', 'a.m.')
    .replace('PM', 'p.m.')
    .replace(/\u202f/g, ' ');

/** A day ("2026-10-27") in words: "Tue 27 Oct". */
export function dayWords(date: string): string {
  const d = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  const f = (o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', ...o }).format(d);
  return `${f({ weekday: 'short' })} ${f({ day: 'numeric' })} ${f({ month: 'short' })}`;
}

/** When, as a clock in the company's zone: "10:52 a.m.". */
export function clockWords(iso: string, timeZone?: string): string {
  return civil(
    new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(iso)),
  );
}

/** The day ("2026-10-26") an instant falls on in the company's zone. */
export function dayOf(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** "Mon 26 Oct, 10:44 a.m.". */
export const momentWords = (iso: string, timeZone?: string) =>
  `${dayWords(dayOf(iso, timeZone))}, ${clockWords(iso, timeZone)}`;

/** A day `n` days from `date` ("2026-10-27", 1 → "2026-10-28"). */
export const addDays = (date: string, n: number) =>
  new Date(Date.parse(`${date}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

const n = (s: string | number) => Number(s);
const lines = (k: number) => `${k} ${k === 1 ? 'line' : 'lines'}`;

// ── the places and the table ──

/** The places that keep stock, the warehouse first, each with who has it (the shop, or its tech). */
export function stockPlaces(
  locations: readonly StockLocation[],
  staffNames: ReadonlyMap<string, string>,
): StockPlace[] {
  return orderLocations(locations).map((l) => ({
    id: l.id,
    name: l.name,
    kind: l.kind,
    who:
      l.kind === 'warehouse'
        ? 'The shop'
        : l.staffId && staffNames.get(l.staffId)
          ? shortName(staffNames.get(l.staffId)!)
          : null,
  }));
}

/** What's on hand at our cost: on hand (never below zero) times the moving average, when it's given. */
export function atCost(l: Pick<LevelAnswer, 'onHand' | 'avgCostCents'>): number | undefined {
  if (l.avgCostCents === undefined) return undefined;
  return Math.round(Math.max(0, n(l.onHand)) * n(l.avgCostCents));
}

function levelOf(l: LevelAnswer): StockLevel {
  const cost = atCost(l);
  return {
    onHand: l.onHand,
    min: l.min,
    max: l.max,
    countNeeded: l.countNeeded,
    // stock.levels doesn't say when it was last counted: absent, so the table draws no column of it
    ...(cost !== undefined ? { costCents: cost } : {}),
  };
}

/**
 * stock.levels' answer as StockTable's rows for these places: an item a row, a level per place that
 * keeps it, its code, unit and category from the catalog; by category, then by name.
 */
export function itemRows(
  levels: readonly LevelAnswer[],
  placeIds: readonly string[],
  catalog: ReadonlyMap<string, CatalogEntry>,
): StockItemRow[] {
  const at = new Set(placeIds);
  const rows = new Map<string, StockItemRow>();
  for (const l of levels) {
    if (!at.has(l.locationId)) continue;
    const c = catalog.get(l.itemId);
    const row = rows.get(l.itemId) ?? {
      itemId: l.itemId,
      itemName: c?.name ?? l.itemName,
      code: c?.code ?? '',
      unit: c?.unit ?? 'each',
      category: c?.category ?? NO_CATEGORY,
      levels: {},
    };
    row.levels[l.locationId] = levelOf(l);
    rows.set(l.itemId, row);
  }
  const last = (r: StockItemRow) => (r.category.id === NO_CATEGORY.id ? 1 : 0);
  return [...rows.values()].sort(
    (a, b) =>
      last(a) - last(b) ||
      a.category.name.localeCompare(b.category.name) ||
      a.itemName.localeCompare(b.itemName),
  );
}

const at = (levels: readonly LevelAnswer[], placeId: string) =>
  levels.filter((l) => l.locationId === placeId);

/** The places switch: Every place, then each place, how many are below min said beside all but the chosen. */
export function placeTabs(
  places: readonly StockPlace[],
  levels: readonly LevelAnswer[],
  chosen: string,
): StatusTab[] {
  return [
    { id: 'every', label: 'Every place' },
    ...places.map((p): StatusTab => {
      const here = at(levels, p.id);
      const below = here.filter(isBelowMin).length;
      const says =
        p.id === chosen ? undefined : !here.length ? 'new' : below ? `${below} below min` : undefined;
      return {
        id: p.id,
        label: p.name,
        ...(p.who ? { sub: p.who } : {}),
        ...(says ? { says } : {}),
        urgent: !!below && says !== 'new',
      };
    }),
  ];
}

export interface PlaceFactsOpts {
  /** The refill on its way to a truck, in words ("Tue 27 Oct"); null: none open. */
  refillDay?: string | null;
  /** Its last restock: the day, and how many lines it brought. */
  lastRestock?: { day: string; lines: number } | null;
}

/** One place's facts: below min, no min, to count, its last restock; at cost for whoever sees our cost. */
export function placeFacts(
  place: StockPlace,
  levels: readonly LevelAnswer[],
  opts: PlaceFactsOpts = {},
): FigureItem[] {
  const here = at(levels, place.id);
  const below = here.filter(isBelowMin).length;
  const facts: FigureItem[] = [
    {
      label: 'Below minimum',
      value: String(below),
      sub: !below
        ? 'all at their minimum or above'
        : place.kind === 'warehouse'
          ? 'the weekly reorder buys these'
          : opts.refillDay
            ? `the refill for ${opts.refillDay} brings these`
            : 'the next refill brings these',
      bad: below > 0,
    },
    { label: 'No minimum', value: String(here.filter(hasNoMin).length), sub: 'never refilled' },
    {
      label: 'To count',
      value: String(here.filter((l) => l.countNeeded).length),
      sub: 'flagged for a count',
    },
  ];
  if (opts.lastRestock)
    facts.push({
      label: 'Last restock',
      value: opts.lastRestock.day,
      sub: `${lines(opts.lastRestock.lines)} on it`,
    });
  const costs = here.map(atCost);
  if (costs.length && costs.every((c) => c !== undefined))
    facts.push({
      label: 'At cost',
      value: money(costs.reduce((s: number, c) => s + c!, 0)),
      sub: `on ${place.name}`,
    });
  return facts;
}

/** Every place's facts, summed over the places. */
export function everyFacts(levels: readonly LevelAnswer[], placeIds: readonly string[]): FigureItem[] {
  const ids = new Set(placeIds);
  const all = levels.filter((l) => ids.has(l.locationId));
  const below = all.filter(isBelowMin).length;
  return [
    { label: 'Below minimum', value: String(below), sub: 'across every place', bad: below > 0 },
    { label: 'No minimum', value: String(all.filter(hasNoMin).length), sub: 'never refilled' },
    { label: 'To count', value: String(all.filter((l) => l.countNeeded).length), sub: 'flagged for a count' },
  ];
}

// ── a count sent in ──

/** CountReview's reasons, as stockCounts.accept takes them (a reason it has no word for: other, said). */
export const REASON_OF: Record<string, { reason: string; note?: string }> = {
  'Used, not logged': { reason: 'used_not_logged' },
  'Logged, not used': { reason: 'other', note: 'Logged, not used' },
  'Damaged or thrown out': { reason: 'damaged' },
  Found: { reason: 'found' },
  'Counted wrong last time': { reason: 'miscount' },
};

const costOf = (levels: readonly LevelAnswer[], itemId: string): number | undefined => {
  const c = levels.find((l) => l.itemId === itemId && l.avgCostCents !== undefined)?.avgCostCents;
  return c === undefined ? undefined : n(c);
};

/**
 * A count sent in from a truck as CountReview draws it: each line counted beside the books as they stood
 * when it was counted (stockCounts.open's expected); an item counted that the truck has no level for
 * isn't on its list (an extra).
 */
export function countView(
  c: OpenCount,
  truckLevels: readonly LevelAnswer[],
  catalog: ReadonlyMap<string, CatalogEntry>,
  allLevels: readonly LevelAnswer[] = truckLevels,
): { lines: CountLine[]; extra: CountExtra[] } {
  const out: CountLine[] = [];
  const extra: CountExtra[] = [];
  for (const l of c.lines) {
    if (l.counted === null || l.expected === null) continue;
    // a later count of it is posted: posting this one leaves it as that count did (QA-863)
    if (l.countedAgain?.posted) continue;
    const cat = catalog.get(l.itemId);
    const lv = truckLevels.find((x) => x.itemId === l.itemId);
    const each = costOf(allLevels, l.itemId);
    const base = {
      itemId: l.itemId,
      itemName: cat?.name ?? l.name,
      code: cat?.code ?? '',
      unit: cat?.unit ?? 'each',
      counted: l.counted,
      ...(each !== undefined ? { eachCents: each } : {}),
    };
    if (!lv) extra.push(base);
    else
      out.push({
        ...base,
        category: cat?.category ?? NO_CATEGORY,
        shows: l.expected,
        flagged: lv.countNeeded,
      });
  }
  return { lines: out, extra };
}

/** What a count sent in says in the column: "Sent 10:52 a.m.: 3 differ from what it shows, 1 not on its list". */
export function countSays(
  c: Pick<OpenCount, 'submittedAt'>,
  view: { lines: CountLine[]; extra: CountExtra[] },
  timeZone?: string,
): string {
  const differ = view.lines.filter((l) => n(l.counted) !== n(l.shows)).length;
  const parts = [
    differ ? `${differ} differ from what it shows` : 'all of it matches what it shows',
    ...(view.extra.length ? [`${view.extra.length} not on its list`] : []),
  ];
  return `${c.submittedAt ? `Sent ${clockWords(c.submittedAt, timeZone)}` : 'Sent in'}: ${parts.join(', ')}`;
}

/** stockCounts.accept's input from CountReview's decision. */
export function acceptInput(countId: string, d: CountDecision, view: { lines: CountLine[] }) {
  const recounts = Object.entries(d.recounts)
    .filter(([, note]) => note.trim())
    .map(([itemId, note]) => ({ itemId, note: note.trim() }));
  const again = new Set(recounts.map((r) => r.itemId));
  const reasons = view.lines
    .filter((l) => n(l.counted) !== n(l.shows) && !again.has(l.itemId) && d.reasons[l.itemId])
    .map((l) => {
      const r = REASON_OF[d.reasons[l.itemId]!] ?? { reason: 'other', note: d.reasons[l.itemId] };
      return { itemId: l.itemId, reason: r.reason, ...(r.note ? { note: r.note } : {}) };
    });
  const extras = Object.entries(d.extra).map(([itemId, c]) => ({ itemId, keep: c === 'add' }));
  return { countId, reasons, recounts, extras };
}

// ── a refill, and a restock received ──

export interface PicklistAsk {
  requestId: string;
  staffName: string;
  jobNumber: string | null;
  quantity: string;
  note: string | null;
  requestedAt: string;
  status: 'open' | 'got' | 'not_got';
}
/** picklists.get's answer. */
export interface Picklist {
  id: string;
  locationId: string;
  date: string;
  status: 'open' | 'posted' | 'loaded';
  receivedAt: string | null;
  lines: {
    itemId: string;
    itemName: string;
    quantity: string;
    why: 'used_yesterday' | 'below_min' | 'asked' | 'short';
    shortQty: string;
    pickedQty: string | null;
    receivedQty: string | null;
    note: string | null;
    asks: PicklistAsk[];
    shorts: { issueId: string; picklistId: string; quantity: string }[];
  }[];
  issues: {
    id: string;
    itemId: string;
    itemName: string;
    kind: 'short' | 'extra';
    quantity: string;
    choice: 'next' | 'chase' | 'keep' | 'back' | null;
  }[];
}

const WHY: Record<Picklist['lines'][number]['why'], string> = {
  below_min: 'Below its minimum: tops up to its maximum',
  used_yesterday: 'Used yesterday: puts back what was used',
  asked: 'Asked for',
  short: 'What a restock came short',
};

/** The refill's name: "Tomorrow’s refill", "Today’s refill" or "The refill for Thu 29 Oct". */
export function refillName(date: string, today: string): string {
  if (date === today) return 'Today’s refill';
  if (date === addDays(today, 1)) return 'Tomorrow’s refill';
  return `The refill for ${dayWords(date)}`;
}

/** An open refill's lines as RefillLines draws them: why each is on it, the asks it carries, the warehouse's stock. */
export function refillLinesOf(
  p: Picklist,
  opts: {
    first: string;
    warehouse: readonly LevelAnswer[];
    catalog: ReadonlyMap<string, CatalogEntry>;
    levels: readonly LevelAnswer[];
    timeZone?: string;
  },
): RefillLine[] {
  return p.lines.map((l) => {
    const unit = opts.catalog.get(l.itemId)?.unit ?? 'each';
    const open = l.asks.filter((a) => a.status === 'open');
    const asks = open.map(
      (a) =>
        `${firstName(a.staffName)}’s ask, ${momentWords(a.requestedAt, opts.timeZone)}${
          a.jobNumber ? `: for ${a.jobNumber}` : ''
        }${a.note ? ` (“${a.note}”)` : ''}`,
    );
    const short = l.shorts.reduce((s, x) => s + n(x.quantity), 0);
    const why = [
      ...(l.why === 'asked' && asks.length ? [] : l.why === 'short' && short ? [] : [WHY[l.why]]),
      ...asks,
      ...(short
        ? [
            `Short on an earlier restock: ${qtyWords(short, unit)} ${short === 1 ? 'comes' : 'come'} on this one`,
          ]
        : []),
      ...(l.note ? [`The office’s note: ${l.note}`] : []),
    ].join('. ');
    const wh = opts.warehouse.find((w) => w.itemId === l.itemId);
    const each = costOf(opts.levels, l.itemId);
    return {
      itemId: l.itemId,
      itemName: opts.catalog.get(l.itemId)?.name ?? l.itemName,
      unit,
      kind: open.length ? 'ask' : short ? 'short' : 'below',
      qty: String(n(l.quantity)),
      why,
      ...(wh ? { warehouseHas: qtyWords(wh.onHand, unit), warehouseLow: isBelowMin(wh) } : {}),
      ...(each !== undefined ? { eachCents: each } : {}),
    };
  });
}

/** What a refill says in the column: "For Tue 27 Oct: 2 lines, 1 ask to approve". */
export function refillSays(p: Picklist): string {
  const asks = p.lines.filter((l) => l.asks.some((a) => a.status === 'open')).length;
  return `For ${dayWords(p.date)}: ${lines(p.lines.length)}${
    asks ? `, ${asks} ${asks === 1 ? 'ask' : 'asks'} to approve` : ''
  }`;
}

/** The note a removed line's asks are answered with: the tech reads it on the iPad. */
export const REMOVED_NOTE = 'The office took it off this refill.';

/**
 * picklists.setLine's calls for what the office changed on RefillLines before Send: a line left off is
 * removed (its asks answered not got, with the note); one trimmed takes its new amount.
 */
export function refillChanges(p: Picklist, sent: readonly { itemId: string; qty: string }[]) {
  const out: Record<string, unknown>[] = [];
  for (const l of p.lines) {
    const s = sent.find((x) => x.itemId === l.itemId);
    if (!s)
      out.push({
        picklistId: p.id,
        itemId: l.itemId,
        remove: true,
        ...(l.asks.some((a) => a.status === 'open') ? { note: REMOVED_NOTE } : {}),
      });
    else if (n(s.qty) !== n(l.quantity))
      out.push({ picklistId: p.id, itemId: l.itemId, quantity: s.qty.trim() });
  }
  return out;
}

/** A restock received with something still to decide: what the tech said didn't match. */
export const toDecide = (p: Picklist) => !!p.receivedAt && p.issues.some((i) => i.choice === null);

/** A PO's line still owed to a truck: back-ordered by the vendor. */
export interface BackOrder {
  poId: string;
  number: string;
  vendor: string | null;
  lines: { itemId: string; description: string; open: string; unitCostCents: number }[];
}

/** purchaseOrders.list's orders for this place with lines back-ordered. */
export function backOrdersFor(
  orders: readonly {
    id: string;
    number: string;
    vendorId: string;
    locationId: string;
    cancelled: unknown;
    lines: {
      itemId: string;
      description: string;
      quantity: string;
      receivedQty: string;
      cancelledQty: string;
      backOrdered: boolean;
      unitCostCents: number;
    }[];
  }[],
  placeId: string,
  vendors: ReadonlyMap<string, string>,
): BackOrder[] {
  return orders.flatMap((o) => {
    if (o.locationId !== placeId || o.cancelled) return [];
    const owed = o.lines
      .filter((l) => l.backOrdered)
      .map((l) => ({
        itemId: l.itemId,
        description: l.description,
        open: String(n(l.quantity) - n(l.receivedQty) - n(l.cancelledQty)),
        unitCostCents: l.unitCostCents,
      }))
      .filter((l) => n(l.open) > 0);
    return owed.length
      ? [{ poId: o.id, number: o.number, vendor: vendors.get(o.vendorId) ?? null, lines: owed }]
      : [];
  });
}

/** A back-order's issue's id: the PO's, marked so it isn't taken for a restock's. */
export const PO_ISSUE = 'po:';

/** A restock received, and the back-orders owed to its truck, as RestockReceived draws them. */
export function restockView(
  p: Picklist | null,
  backOrders: readonly BackOrder[],
  opts: {
    place: string;
    first: string;
    catalog: ReadonlyMap<string, CatalogEntry>;
    levels: readonly LevelAnswer[];
    seesCost: boolean;
    timeZone?: string;
  },
): { head: string; asSent?: string; issues: RestockIssue[]; says: string } {
  const unitOf = (id: string) => opts.catalog.get(id)?.unit ?? 'each';
  const nameOf = (id: string, fallback: string) => opts.catalog.get(id)?.name ?? fallback;
  const cost = (id: string, q: string | number) => {
    const each = costOf(opts.levels, id);
    return opts.seesCost && each !== undefined ? { costCents: Math.round(n(q) * each) } : {};
  };
  const issues: RestockIssue[] = [];
  const says: string[] = [];
  for (const i of p?.issues.filter((x) => x.choice === null) ?? []) {
    const line = p!.lines.find((l) => l.itemId === i.itemId);
    const unit = unitOf(i.itemId);
    const name = nameOf(i.itemId, i.itemName);
    const q = qtyWords(i.quantity, unit);
    const picked = line?.pickedQty ?? null;
    const came = line?.receivedQty ?? null;
    if (i.kind === 'short') {
      issues.push({
        id: i.id,
        kind: 'received',
        title: `${name}: ${qtyWords(picked ?? '0', unit)} sent, ${qtyWords(came ?? '0', unit)} came`,
        sub: `${q} didn’t come: back on the warehouse’s books until you decide.`,
        label: `The ${q} that didn’t come`,
        choices: [
          {
            value: 'next',
            label: `Put the ${q} on the next refill`,
            says: `The next refill for ${opts.place} brings ${q} more.`,
          },
          {
            value: 'chase',
            label: 'Ask the warehouse to look for them',
            says: `The warehouse’s ${name} is flagged for a count: check the shelf and the pick.`,
          },
        ],
        ...cost(i.itemId, i.quantity),
      });
      says.push(`${qtyWords(came ?? '0', unit)} of ${qtyWords(picked ?? '0', unit)} ${name} came`);
    } else {
      issues.push({
        id: i.id,
        kind: 'received',
        title:
          line && picked !== null
            ? `${name}: ${qtyWords(picked, unit)} sent, ${qtyWords(came ?? picked, unit)} came`
            : `${name}: not sent, ${q} came`,
        sub: `On ${opts.place}, not on its books yet: nothing has moved.`,
        label: `The ${name} nobody sent`,
        choices: [
          {
            value: 'back',
            label: `${opts.first} brings it back`,
            says: `It goes back on the warehouse’s shelf when ${opts.first} is next at the shop; nothing moves until then.`,
          },
          {
            value: 'keep',
            label: `Keep it on ${opts.place}`,
            says: `The warehouse’s books move ${q} onto ${opts.place}.`,
          },
        ],
        ...cost(i.itemId, i.quantity),
      });
      says.push(`${q} ${name} nobody sent`);
    }
  }
  for (const b of backOrders) {
    const what = b.lines.map((l) => `${l.description}: ${qtyWords(l.open)}`).join(', ');
    const owed = b.lines.reduce((s, l) => s + n(l.open) * l.unitCostCents, 0);
    issues.push({
      id: `${PO_ISSUE}${b.poId}`,
      kind: 'backorder',
      title: `${what}, back-ordered`,
      sub: `${b.number}${b.vendor ? `, ${b.vendor}` : ''}: the rest of the order came.`,
      label: `What ${b.number} still owes`,
      chosen: 'wait',
      choices: [
        { value: 'wait', label: 'Keep it on order', says: `${b.number} stays open for them.` },
        {
          value: 'cancel',
          label: 'Cancel what’s owed',
          says: `${b.number} closes without them; a vendor sent the order is told it’s cancelled.`,
        },
      ],
      ...(opts.seesCost ? { costCents: owed } : {}),
    });
    says.push(`${b.lines.map((l) => `${qtyWords(l.open)} ${l.description}`).join(', ')} back-ordered`);
  }
  if (!p)
    return {
      head: `What the vendors still owe ${opts.place}.`,
      issues,
      says: says.join('; '),
    };
  const asSent = p.lines.filter(
    (l) => l.receivedQty !== null && l.pickedQty !== null && n(l.receivedQty) === n(l.pickedQty),
  );
  return {
    head: `From the warehouse, for ${dayWords(p.date)}. ${opts.first} received it${
      p.receivedAt ? ` ${momentWords(p.receivedAt, opts.timeZone)}` : ''
    }: ${asSent.length} of ${lines(p.lines.length)} as sent.`,
    ...(asSent.length
      ? {
          asSent: `${asSent.length} came as sent: ${asSent
            .map((l) => `${nameOf(l.itemId, l.itemName)} ${qtyWords(l.pickedQty!, unitOf(l.itemId))}`)
            .join(', ')}.`,
        }
      : {}),
    issues,
    says: says.join('; '),
  };
}

/** What RestockReceived's Close decided: restockIssues.decide's choices, and the POs to cancel. */
export function restockDecisions(p: Picklist | null, chosen: Record<string, string>) {
  const choices = (p?.issues ?? [])
    .filter((i) => i.choice === null && chosen[i.id])
    .map((i) => ({ issueId: i.id, choice: chosen[i.id]! }));
  const cancel = Object.entries(chosen)
    .filter(([id, c]) => id.startsWith(PO_ISSUE) && c === 'cancel')
    .map(([id]) => id.slice(PO_ISSUE.length));
  return { decide: p && choices.length ? { picklistId: p.id, choices } : null, cancel };
}

// ── a receipt to file ──

/** jobPurchases.list's receipt, with what line filing needs (its lines' ids and items). */
export interface Receipt {
  id: string;
  number: string;
  jobId: string | null;
  jobNumber: string | null;
  staffName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  supplierName: string | null;
  receiptRef: string | null;
  purchasedOn: string;
  totalCents?: number;
  taxes: { code: string; taxCents?: number }[];
  lines: {
    id: string;
    description: string;
    quantity: string;
    amountCents?: number | null;
    itemId: string | null;
  }[];
  amounts: { totalCents: number; costCents: number; taxes: { code: string; taxCents: number }[] } | null;
  receipt: { fileId: string; kind: string; status: string; url: string | null } | null;
  rowVersion: number;
  status: string;
}

export const supplierOf = (r: Pick<Receipt, 'vendorName' | 'supplierName'>) =>
  r.vendorName ?? r.supplierName ?? 'A store';

/** Where a line goes, as ReceiptFiling's select names it: the receipt's job, onto the truck, the shop. */
export const TRUCK = 'truck';
export const SHOP = 'shop';

/**
 * A receipt as ReceiptFiling draws it: each line's amount, the catalog part it is and where it goes
 * first (its job, else the shop); null when a line has no amount to show (only for those who see
 * what was paid), so it's filed whole in Accounting › Receipts.
 */
export function receiptView(
  r: Receipt,
  opts: {
    place: StockPlace;
    catalog: ReadonlyMap<string, CatalogEntry>;
    truckLevels: readonly LevelAnswer[];
    seesCost: boolean;
    paid: string;
  },
): {
  lines: ReceiptLine[];
  destinations: ReceiptDestination[];
  subtotalCents: number;
  gstCents: number;
  pstCents: number;
  totalCents: number;
} | null {
  if (!r.lines.length || r.lines.some((l) => l.amountCents == null)) return null;
  const total = r.amounts?.totalCents ?? r.totalCents;
  if (total === undefined) return null;
  const job = r.jobId ? (r.jobNumber ?? 'The job') : null;
  const destinations: ReceiptDestination[] = [
    ...(job ? [{ value: job, label: job, kind: 'job' as const }] : []),
    { value: TRUCK, label: `Onto ${opts.place.name}`, kind: 'van' },
    { value: SHOP, label: 'The shop (overhead)', kind: 'shop' },
  ];
  const lines = r.lines.map((l): ReceiptLine => {
    const c = l.itemId ? opts.catalog.get(l.itemId) : undefined;
    const match: CatalogMatch | null = l.itemId
      ? {
          itemId: l.itemId,
          itemName: c?.name ?? l.description,
          code: c?.code ?? '',
          ...(opts.seesCost && c?.costCents != null ? { catalogEachCents: c.costCents } : {}),
        }
      : null;
    const on = l.itemId ? opts.truckLevels.find((x) => x.itemId === l.itemId) : undefined;
    return {
      id: l.id,
      text: l.description,
      qty: String(n(l.quantity)),
      eachCents: Math.round(l.amountCents! / Math.max(n(l.quantity), 0.001)),
      match,
      ...(match ? {} : { oneOff: l.description }),
      // a job's receipt goes on the job; with none, a part the truck carries goes onto it, the rest is overhead
      to: job ?? (match && on ? TRUCK : SHOP),
      ...(on ? { vanOn: String(n(on.onHand)) } : {}),
    };
  });
  const subtotal = r.lines.reduce((s, l) => s + l.amountCents!, 0);
  const gst = (r.amounts?.taxes ?? r.taxes).find((t) => t.code === 'GST')?.taxCents ?? 0;
  return {
    lines,
    destinations,
    subtotalCents: subtotal,
    gstCents: gst,
    pstCents: Math.max(0, total - subtotal - gst),
    totalCents: total,
  };
}

/** What a receipt says in the column: "$66.08, 3 lines, 1 part not in the catalog". */
export function receiptSays(r: Receipt): string {
  const total = r.amounts?.totalCents ?? r.totalCents;
  const loose = r.lines.filter((l) => !l.itemId).length;
  return [
    ...(total !== undefined ? [money(total)] : []),
    lines(r.lines.length),
    ...(loose ? [`${loose} ${loose === 1 ? 'part' : 'parts'} not in the catalog`] : []),
  ].join(', ');
}

/** jobPurchases.approve's `lines` from ReceiptFiling's filing: each line to its job, onto the truck or the shop. */
export function filingLines(r: Receipt, filing: ReceiptFilingDecision, truckId: string) {
  return r.lines.map((l) => {
    const to = filing.to[l.id];
    const item = filing.items[l.id] ?? null;
    return {
      lineId: l.id,
      to:
        to === TRUCK
          ? { truck: truckId }
          : to === SHOP || !r.jobId
            ? { shop: true as const }
            : { jobId: r.jobId },
      ...(item !== l.itemId ? { itemId: item } : {}),
    };
  });
}

/** The catalog's find, over the parts read: name or code, a few words. */
export function findIn(catalog: ReadonlyMap<string, CatalogEntry>, words: string): CatalogMatch[] {
  const w = words.toLowerCase();
  return [...catalog.values()]
    .filter((c) => `${c.name} ${c.code}`.toLowerCase().includes(w))
    .map((c) => ({ itemId: c.id, itemName: c.name, code: c.code }));
}
